import produce from 'immer';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { IndexRange } from '../../../../common-types';
import {
  ApiDataQueryType,
  ApiDataQueryWithTypeInfo,
  ApiMasterDataQueryInfiniteLoadingResponse,
  ApiMasterDataQueryResponse,
} from '../../api/api-interfaces';
import { BenchmarkConfig } from '../../api/master-data-service';
import { DataTypes } from '../../constants/constants';
import {
  GetResponseForAdvancedQueryService,
  GetResponseForMasterQueryService,
} from '../../services/GetResponseForQueryService';
import { ChartDataFetchError } from '../../v2/common/components/errors/components/ChartDataFetchError';
import { removeDuplicates } from '../../v2/common/utils';
import {
  useLatestVersionsContext,
  useLegacyDomainScopedStoresContext,
} from '../../v2/context/contexts';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from './constants';

export interface RenderCallback {
  data:
    | ApiMasterDataQueryResponse
    | ApiMasterDataQueryInfiniteLoadingResponse
    | null;
  refetch: (offsetParams?: IndexRange) => Promise<void>;
}

interface QueryExecutorProps {
  children: (state: RenderCallback) => React.ReactNode;
  onReady?: () => void;
  onLoading?: () => void;
  queryConfig: QueryConfig;
  benchmarkConfig?: BenchmarkConfig;
  withOffset?: boolean;
}

interface QueryExecutorState {
  data: ApiMasterDataQueryResponse | null;
  status: Readiness;
  error: Error | null;
}

enum Readiness {
  NOT_READY = 'NOT_READY',
  ERROR = 'ERROR',
  READY = 'READY',
}

const ErrorContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface QueryConfig {
  queriesWithType: ApiDataQueryWithTypeInfo[];
  resultsFormatter?: (
    responses: ApiMasterDataQueryResponse[]
  ) => ApiMasterDataQueryResponse;
}

// Observer is needed here if the callback component uses any observable
const QueryExecutor = observer((props: QueryExecutorProps) => {
  const legacyDomainScopedStores = useLegacyDomainScopedStoresContext();
  const { cachedMasterDataApiService, loadingStateStore } =
    legacyDomainScopedStores;
  const latestVersions = useLatestVersionsContext();
  const { queryConfig, benchmarkConfig, withOffset, onLoading, onReady } =
    props;

  const [dataState, setDataState] = useState<QueryExecutorState>({
    data: null,
    status: Readiness.NOT_READY,
    error: null,
  });

  const defaultResultsFormatter = (responses: ApiMasterDataQueryResponse[]) => {
    const dataPoints = responses.flatMap(
      (r) => r.dataPoints?.filter((x) => x) ?? []
    );
    return { dataPoints };
  };

  const requestCounterRef = useRef(0);
  const incRequestCounter = () => {
    requestCounterRef.current++;
  };
  const fetch = async (offsetParams?: IndexRange) => {
    incRequestCounter();
    const currRequestNum = requestCounterRef.current;
    const freshDataFetch = !offsetParams && withOffset;
    const newQueryConfig = produce(queryConfig, (draft) => {
      draft.queriesWithType.forEach((typedQuery) => {
        if (withOffset) {
          typedQuery.query.offset = offsetParams?.startIndex ?? DEFAULT_OFFSET;
          typedQuery.query.limit = offsetParams
            ? offsetParams?.stopIndex - offsetParams?.startIndex
            : DEFAULT_LIMIT;
        }
      });
    });

    setDataState((prev) => ({
      data: offsetParams ? prev.data : null,
      status: Readiness.NOT_READY,
      error: null,
    }));

    try {
      onLoading && onLoading();
      loadingStateStore.loadStarted();

      const responses: ApiMasterDataQueryResponse[] = await Promise.all(
        newQueryConfig.queriesWithType.map(async (queryWithType) => {
          return match(queryWithType)
            .with({ type: ApiDataQueryType.ApiMasterDataQuery }, (qWithType) =>
              GetResponseForMasterQueryService(
                qWithType.query,
                cachedMasterDataApiService,
                latestVersions[qWithType.query.dataType as DataTypes],
                benchmarkConfig
              )
            )
            .with(
              { type: ApiDataQueryType.ApiMasterDataAdvancedQuery },
              (qWithType) =>
                GetResponseForAdvancedQueryService(
                  qWithType.query,
                  cachedMasterDataApiService,
                  benchmarkConfig
                )
            )
            .with(
              { type: ApiDataQueryType.ApiMasterDataMovementQuery },
              (qWithType) =>
                cachedMasterDataApiService.executeMovementQuery(qWithType.query)
            )
            .exhaustive();
        })
      );

      const response: ApiMasterDataQueryResponse =
        newQueryConfig.resultsFormatter
          ? newQueryConfig.resultsFormatter(responses)
          : defaultResultsFormatter(responses);

      const isLatestRequest = currRequestNum === requestCounterRef.current;
      if (isLatestRequest) {
        let updatedData: ApiMasterDataQueryResponse | null;
        if (withOffset) {
          updatedData = {
            dataPoints: freshDataFetch
              ? response.dataPoints
              : removeDuplicates([
                  ...(dataState.data?.dataPoints ?? []),
                  ...response.dataPoints,
                ]),
          };
        } else {
          updatedData = response.dataPoints ? response : { dataPoints: [] };
        }
        setDataState(() => ({
          data: updatedData,
          status: Readiness.READY,
          error: null,
        }));
      }
    } catch (err) {
      // tslint:disable-next-line:no-console
      console.error(err);
      setDataState(() => ({
        data: null,
        status: Readiness.ERROR,
        error: err as Error,
      }));
    } finally {
      loadingStateStore.loadFinished();
      onReady && onReady();
    }
  };

  useEffect(() => {
    void fetch();
  }, []);

  useEffect(() => {
    void fetch();
  }, [queryConfig, benchmarkConfig]);

  useEffect(() => {
    void fetch();
  }, [withOffset]);

  const { children } = props;

  if (dataState.status === Readiness.ERROR && dataState.error) {
    return (
      <ErrorContainer>
        <ChartDataFetchError
          retry={fetch}
          errorData={{ error: dataState.error }}
        />
      </ErrorContainer>
    );
  }

  return (
    <>
      {children({
        data: dataState.data,
        refetch: fetch,
      })}
    </>
  );
});

export default QueryExecutor;
